<template>
  <fragment>
    <loader v-if="!store.initialized && reportsFetching" />
    <div v-else class="container dashboard">
      <div class="row" style="margin-bottom: 2.3rem">
        <div class="col-12">
          <college-header />
        </div>
      </div>

      <primary-cards />

      <div class="row" style="margin-top: 3rem">
        <div class="col-12">
          <div class="dashboard-tabs">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              type="button"
              :class="selectedTab === tab.id && 'active'"
              :disabled="reportsFetching"
              @click="selectTab(tab.id)"
            >
              {{ tab.title }}
            </button>
          </div>
        </div>
        <credits-tab v-if="selectedTab === 'credits'" />
        <sessions-tab v-else-if="selectedTab === 'sessions'" />
        <students-tab-anonymized v-else-if="selectedTab === 'students' && anonymizeStudents" />
        <students-tab v-else-if="selectedTab === 'students'"/>
        <feedback-tab v-else-if="selectedTab === 'feedback'"/>
        <workshops-tab v-else-if="selectedTab === 'workshops'"/>
      </div>
    </div>

    <feedback form="collegeAdminDashboard" />

    <vue-accessible-modal>
      <template v-slot:backdrop />
    </vue-accessible-modal>
  </fragment>
</template>

<script>
import UserClient from '@/api/UserClient';
import Loader from '@/components/Loader.vue';
import CollegeHeader from './components/CollegeHeader.vue';
import PrimaryCards from './components/PrimaryCards.vue';
import StudentsTab from './components/Tabs/StudentsTab.vue';
import StudentsTabAnonymized from './components/Tabs/StudentsTabAnonymized.vue';
import SessionsTab from './components/Tabs/Sessions/SessionsTab.vue';
import CreditsTab from './components/Tabs/Credits/CreditsTab.vue';
import FeedbackTab from './components/Tabs/Feedback/FeedbackTab.vue';
import WorkshopsTab from './components/Tabs/Workshops/WorkshopsTab.vue';
import Feedback from '@/user-engagement/Feedback.vue';
import Store from './store';

export default {
  name: 'CollegeAdminDashboard',

  components: {
    Loader,
    CollegeHeader,
    PrimaryCards,
    StudentsTab,
    StudentsTabAnonymized,
    SessionsTab,
    CreditsTab,
    FeedbackTab,
    WorkshopsTab,
    Feedback,
  },

  data() {
    return {
      userClient: new UserClient(this.$http),
      store: new Store(),
      anonymizeStudents: false,

      selectedTab: 'credits',
      tabs: [
        { id: 'credits', title: 'Credits' },
        { id: 'sessions', title: 'Sessions' },
        { id: 'students', title: 'Users' },
        { id: 'feedback', title: 'Feedback' },
        { id: 'workshops', title: 'Workshops' },
      ]
    };
  },

  provide() {
    return this.store.provides;
  },

  async created() {
    const { id } = this.$route.query;

    if(id && this.$store.getters.currentUser.isAdmin)
      await this.loadFromId(id)
    else
      await this.loadFromUser();
  },

  computed:{
    reportsFetching(){
      if(!this.store.initialized)
        return true;

      return this.store.provides.collegeDetailsReport.loading 
        || this.store.provides.credits.loading 
        || this.store.provides.demographicsReport.loading 
        || this.store.provides.registrationsReport.loading 
        || this.store.provides.globalMetrics.loading 
        || this.store.provides.sessionsMetricsReport.loading 
        || this.store.provides.sessionsReport.loading 
        || this.store.provides.studentDetailsReport.loading 
        || this.store.provides.uniqueStudents.loading 
        || this.store.provides.uniqueCounselors.loading 
        || this.store.provides.outOfPocketStudents.loading 
        || this.store.provides.feedbackReport.loading 
        || this.store.provides.feedbackMetrics.loading
        || this.store.provides.customUserTags.loading
    }
  },

  methods: {
    selectTab(tab) {
      this.selectedTab = tab;
    },
    async loadFromUser() {
      const college = this.$store.getters.currentUser.school;
      if (!college) {
        return;
      }

      document.title = `${college.name} | Dashboard | BetterMynd`;

      this.anonymizeStudents = await this.$globals.hasFeature('AnonymizeStudents');
      this.store.initialize(this.$http, college.id);

      this.$gtag.event('college_admin_dashboard_view', {
        college_id: college.id,
        college_name: college.name
      });
    },
    async loadFromId(id) {
      this.anonymizeStudents = await this.$globals.schoolHasFeature('AnonymizeStudents', id);
      this.store.initialize(this.$http, id);
    }
  }
};
</script>

<style lang="scss">
@import '@/scss/layouts/_collegeadmin.scss';
</style>
